import { FormControl, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { sendSubmissionAccessLinkEmail } from 'api/submission';
import { EMAIL_REGX } from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import { displayToastMessage } from 'components/ToastMessage';
import { useFormik } from 'formik';
import DefaultLayout from 'layouts/defaultLayout';
import { useNavigate } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import { RetrieveQuoteWrapperStackStyles } from './RetrieveQuote.styles';
import type { RetrieveQuoteFormValues } from './RetrieveQuote.types';

const validationSchema = yup.object({
  email: yup.string().matches(EMAIL_REGX, 'Invalid email format').required(),
});

const RetrieveQuote = () => {
  const themeHook = useTheme();
  const isMobileLayout = useMediaQuery(themeHook.breakpoints.down('sm'));

  const { setFullscreenLoading } = useFullscreenLoader();

  const navigate = useNavigate();

  const formik = useFormik<RetrieveQuoteFormValues>({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values: RetrieveQuoteFormValues) => {
      if (formik.isValid) {
        try {
          setFullscreenLoading(true, 'Sending access link');

          const response = await sendSubmissionAccessLinkEmail(values.email);

          displayToastMessage('SUCCESS', response.message);

          navigate(`/welcome-back?email=${encodeURIComponent(values.email)}`, { replace: true });
        } catch (error) {
          displayBackendErrorMessage(error);
        } finally {
          setFullscreenLoading(false);
        }
      }
    },
  });

  return (
    <DefaultLayout displayQuoteNumber={false} renderStepper={false}>
      <form
        id="retrieveQuoteForm"
        name="retrieveQuoteForm"
        noValidate
        onSubmit={formik.handleSubmit}
        style={{
          display: 'flex',
          flex: 1,
        }}
      >
        <Stack sx={RetrieveQuoteWrapperStackStyles}>
          <Typography variant="h1">Welcome back! Let&apos;s retrieve your quote.</Typography>

          <FormControl variant="standard" fullWidth>
            <Typography variant="h2" mb={1.4}>
              Please enter email address
            </Typography>
            <TextField
              id="email"
              name="email"
              value={formik.values.email}
              type="email"
              onChange={(event) => formik.setFieldValue('email', event.target.value)}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched?.email && formik.errors?.email}
              slotProps={{
                htmlInput: {
                  autoComplete: 'off',
                  'data-test': 'email',
                },
              }}
            />
          </FormControl>

          <Stack gap={2} sx={isMobileLayout ? { mt: 'auto' } : {}}>
            <PrimaryButton
              text="Retrieve"
              fullWidth
              type="submit"
              data-test="retrieve"
              form="retrieveQuoteForm"
            />

            <TertiaryButton
              text="Go back"
              onClick={() => navigate('/', { replace: true })}
              data-test="go_back"
            />
          </Stack>
        </Stack>
      </form>
    </DefaultLayout>
  );
};

export default RetrieveQuote;
